import log from '@/lib/logger';
import { type AppDispatch, type RootState } from '@/store';
import { type NormalizedGridConnectionResponse } from '@/types/client';
import { type ChargePoint, type GridConnection } from '@/types/swagger';
import { type UnknownAction } from '@reduxjs/toolkit';
import { type AxiosError } from 'axios';
import { http } from 'config';
import { loadChargePoints, loadChargePointsByGridConnectionId } from 'features/ChargePoints';
import { loadChargingProfiles } from 'features/ChargingProfiles';
import { loadConnectors } from 'features/Connectors';
import { gridConnectionListSchema, gridConnectionSchema } from 'features/schemas';
import { normalize } from 'normalizr';
import Nprogress from 'nprogress';
import {
  getGridConnectionChargersPower,
  getGridConnectionMetersPower,
} from 'services/gridConnections';
import { callNextPage } from 'util/callNextPage';

import { getAllById, getAllChargePointsById, getById } from './selectors';

// ----------------------------------- Actions ---------------------------------
// Action Types
export enum GridConnectionActionTypes {
  LOAD_GRID_CONNECTIONS = 'grid_connections/load_grid_connections',
  LOAD_GRID_CONNECTION_METERS_POWER = 'grid_connections/load_grid_connection_meters_power',
  LOAD_GRID_CONNECTION_CHARGERS_POWER = 'grid_connections/load_grid_connection_chargers_power',
  SET_SELECTED_GRID_CONNECTION = 'grid_connections/set_selected_grid_connection',
}
// Action Creators
export const loadGridConnections = (gridConnections: Record<number, GridConnection>) => ({
  type: GridConnectionActionTypes.LOAD_GRID_CONNECTIONS,
  payload: gridConnections,
});

export const loadGridConnectionMetersPower = (gridConnectionId: number, power: string) => ({
  type: GridConnectionActionTypes.LOAD_GRID_CONNECTION_METERS_POWER,
  meta: { gridConnectionId },
  payload: power,
});

export const loadGridConnectionChargersPower = (gridConnectionId: number, power: string) => ({
  type: GridConnectionActionTypes.LOAD_GRID_CONNECTION_CHARGERS_POWER,
  meta: { gridConnectionId },
  payload: power,
});

export const setSelectedGridconnection = (gridConnection: GridConnection) => ({
  type: GridConnectionActionTypes.SET_SELECTED_GRID_CONNECTION,
  payload: gridConnection,
});

export interface GridConnectionsState {
  byId: Record<number, GridConnection>;
  selectedGridconnection: GridConnection | null;
  meterPowers: Record<number, string>;
  chargerPowers: Record<number, GridConnection['charge_points_power']>;
  allIds: number[];
}

// ----------------------------------- Reducer ---------------------------------
// Init State
const INIT_STATE: GridConnectionsState = {
  byId: {},
  selectedGridconnection: null,
  meterPowers: {},
  chargerPowers: {},
  allIds: [],
};

const gridConnectionReducer = (state = INIT_STATE, action: UnknownAction) => {
  const { type, meta, payload } = action;
  switch (type) {
    case GridConnectionActionTypes.LOAD_GRID_CONNECTIONS: {
      const gridConnections = payload as Record<number, GridConnection>;
      const gridConnectionIds = Object.keys(gridConnections).map(Number);
      const chargePointsPower = Object.fromEntries(
        gridConnectionIds.map((id) => [id, gridConnections[id].charge_points_power])
      );
      const newById = Object.fromEntries(
        gridConnectionIds.map((id) => [id, { ...state.byId[id], ...gridConnections[id] }])
      );
      return {
        ...state,
        byId: {
          ...state.byId,
          ...newById,
        },
        chargerPowers: {
          ...state.chargerPowers,
          ...(chargePointsPower as Record<number, GridConnection['charge_points_power']>),
        },
      };
    }
    case GridConnectionActionTypes.LOAD_GRID_CONNECTION_METERS_POWER: {
      const metadata = meta as { gridConnectionId: number };
      return {
        ...state,
        meterPowers: {
          ...state.meterPowers,
          [metadata.gridConnectionId]: payload as string,
        },
      };
    }
    case GridConnectionActionTypes.LOAD_GRID_CONNECTION_CHARGERS_POWER: {
      const metadata = meta as { gridConnectionId: number };
      return {
        ...state,
        chargerPowers: {
          ...state.chargerPowers,
          [metadata.gridConnectionId]: payload as GridConnection['charge_points_power'],
        },
      };
    }
    case GridConnectionActionTypes.SET_SELECTED_GRID_CONNECTION: {
      return {
        ...state,
        selectedGridconnection: payload as GridConnection,
      };
    }
    default:
      return state;
  }
};

export default gridConnectionReducer;

// ----------------------------------- Side Effects ----------------------------
// Thunks
export const fetchGridConnections = (assetId?: number) => async (dispatch: AppDispatch) => {
  Nprogress.start();
  const apiGridConnections = assetId
    ? `company/grid_connections/by_asset/${assetId}/`
    : 'company/grid_connections/';
  const resolve = (data: GridConnection[]) => {
    const normalizedData = normalize(data, gridConnectionListSchema);
    const { grid_connections: gridConnections, charge_points: chargePoints } =
      normalizedData.entities as {
        grid_connections: GridConnection[];
        charge_points: ChargePoint[];
      };
    dispatch(loadGridConnections(gridConnections));
    dispatch(loadChargePoints(chargePoints));
    Nprogress.done();
    return data;
  };
  const reject = (error: AxiosError) => {
    log.error(error);
    Nprogress.done();
    return error;
  };
  return callNextPage(apiGridConnections, resolve, reject);
};

export const fetchGridConnection = (gridConnectionId: number) => async (dispatch: AppDispatch) => {
  Nprogress.start();
  const apiGridConnections = `company/grid_connections/${gridConnectionId}/`;
  return await http
    .get(apiGridConnections)
    .then((response) => {
      const normalizedData: NormalizedGridConnectionResponse = normalize(
        response.data,
        gridConnectionSchema
      );
      const {
        grid_connections: gridConnections,
        charge_points: chargePoints,
        charging_profiles: chargingProfiles,
        connectors,
      } = normalizedData.entities;
      dispatch(setSelectedGridconnection(Object.values(gridConnections)[0]));
      dispatch(loadGridConnections(gridConnections));
      dispatch(loadChargePoints(chargePoints));
      dispatch(loadChargePointsByGridConnectionId(gridConnectionId, chargePoints));
      dispatch(loadConnectors(connectors));
      dispatch(loadChargingProfiles(chargingProfiles));
      Nprogress.done();
      return response.data;
    })
    .catch((error) => {
      log.error(error);
      Nprogress.done();
      return error;
    });
};

export const updateGridConnection =
  (newGridConnection: GridConnection) => async (dispatch: AppDispatch) => {
    try {
      const normalizedData: NormalizedGridConnectionResponse = normalize(
        newGridConnection,
        gridConnectionSchema
      );
      const {
        grid_connections: gridConnections,
        charge_points: chargePoints,
        charging_profiles: chargingProfiles,
        connectors,
      } = normalizedData.entities;
      dispatch(setSelectedGridconnection(Object.values(gridConnections)[0]));
      dispatch(loadGridConnections(gridConnections));
      dispatch(loadChargePoints(chargePoints));
      dispatch(loadConnectors(connectors));
      dispatch(loadChargingProfiles(chargingProfiles));
      return newGridConnection;
    } catch (error) {
      log.error(error);
      return error;
    }
  };

export const fetchGridConnectionMetersPower =
  (gridConnectionId: number) => async (dispatch: AppDispatch) => {
    await getGridConnectionMetersPower(gridConnectionId)
      .then((response) => {
        dispatch(loadGridConnectionMetersPower(gridConnectionId, response.data.power));
      })
      .catch((error) => {
        console.log(error);
      });
  };

export const fetchGridConnectionChargersPower =
  (gridConnectionId: number) => async (dispatch: AppDispatch) => {
    await getGridConnectionChargersPower(gridConnectionId)
      .then((response) => {
        if (response.data?.task_result)
          dispatch(loadGridConnectionChargersPower(gridConnectionId, response.data.task_result));
      })
      .catch((error) => {
        console.log(error);
      });
  };

export const selectGridConnections = (state: RootState) => state.gridConnections.byId;
export const getGridConnectionByIdSelector = (state: RootState, gridConnectionId: number) =>
  state.gridConnections.byId[gridConnectionId];
export const getSelectedGridConnectionSelector = (state: RootState) =>
  state.gridConnections.selectedGridconnection;
export const getMeterPowersSelector = (state: RootState) => state.gridConnections.meterPowers;
export const getChargerPowersSelector = (state: RootState) => state.gridConnections.chargerPowers;

export const pureGetGridConnectionById = (state: RootState, id: number) =>
  state.gridConnections.byId[id];
export const pureGetAllGridConnectionsById = (state: RootState) => state.gridConnections.byId;
export const getAllGridConnectionsById = getAllById(
  pureGetAllGridConnectionsById,
  { charge_points: getAllChargePointsById },
  { charge_points: 'charge_points' }
);
export const getGridConnectionById = getById(
  pureGetGridConnectionById,
  { charge_points: getAllChargePointsById },
  { charge_points: 'charge_points' }
);
